import { QueryClient, DefaultOptions } from '@tanstack/react-query';
import { generateUUID } from '../utils/uuid';

// Medical-specific query configuration
const medicalQueryDefaults: DefaultOptions = {
  queries: {
    // Critical medical data settings
    staleTime: 5 * 60 * 1000, // 5 minutes for medical data
    gcTime: 30 * 60 * 1000, // 30 minutes cache retention
    retry: (failureCount, error: any) => {
      // Don't retry authentication errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      // Retry other errors up to 2 times (reduced from 3)
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Reduce aggressive refetching that's causing multiple calls
    refetchOnWindowFocus: false, // Changed from true
    refetchOnMount: true,
    refetchOnReconnect: true,
  },
  mutations: {
    retry: (failureCount, error: any) => {
      // Don't retry authentication or validation errors
      if (error?.status === 401 || error?.status === 403 || error?.status === 422) {
        return false;
      }
      return failureCount < 1; // Reduced retry attempts
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  },
};

// Create QueryClient with medical configuration
export const queryClient = new QueryClient({
  defaultOptions: medicalQueryDefaults,
});

// Simple localStorage-based persistence for medical data
export const setupQueryPersistence = () => {
  const CACHE_KEY = 'gynhub-medical-cache';
  
  // Load cached queries on startup
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      // Only restore non-sensitive medical data
      const safeKeys = ['loinc', 'reports/templates', 'admin/stats'];
      Object.entries(data).forEach(([key, value]) => {
        if (safeKeys.some(safe => key.includes(safe))) {
          queryClient.setQueryData(JSON.parse(key), value);
        }
      });
    }
  } catch (error) {
    console.warn('Failed to restore query cache:', error);
  }
  
  // Save critical queries to localStorage
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.query.state.status === 'success') {
      try {
        const queryKey = event.query.queryKey;
        const keyString = JSON.stringify(queryKey);
        
        // Only cache safe medical data
        const safeKeys = ['loinc', 'reports/templates', 'admin/stats'];
        if (safeKeys.some(safe => keyString.includes(safe))) {
          const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
          cached[keyString] = event.query.state.data;
          localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
        }
      } catch (error) {
        console.warn('Failed to cache query:', error);
      }
    }
  });
};

// Query keys for consistent caching (aligned with backend endpoints)
export const queryKeys = {
  // Authentication
  auth: {
    user: ['auth', 'user'] as const,
    tokens: ['auth', 'tokens'] as const,
    verify: ['auth', 'verify'] as const,
  },
  
  // Patients
  patients: {
    all: ['patients'] as const,
    list: (filters?: any) => ['patients', 'list', filters] as const,
    detail: (id: string) => ['patients', 'detail', id] as const,
    studies: (patientId: string) => ['patients', patientId, 'studies'] as const,
    timeline: (patientId: string) => ['patients', patientId, 'timeline'] as const,
  },
  
  // DICOM
  dicom: {
    all: ['dicom'] as const,
    study: (studyId: string) => ['dicom', 'study', studyId] as const,
    images: (studyId: string) => ['dicom', 'study', studyId, 'images'] as const,
    metadata: (studyId: string) => ['dicom', 'study', studyId, 'metadata'] as const,
    measurements: (imageId: string) => ['dicom', 'image', imageId, 'measurements'] as const,
    equipment: (studyId: string) => ['dicom', 'study', studyId, 'equipment'] as const,
  },
  
  // Reports
  reports: {
    all: ['reports'] as const,
    list: (filters?: any) => ['reports', 'list', filters] as const,
    detail: (id: string) => ['reports', 'detail', id] as const,
    byPatient: (patientId: string) => ['reports', 'byPatient', patientId] as const,
    templates: ['reports', 'templates'] as const,
    template: (id: string) => ['reports', 'template', id] as const,
    config: (reportType: string) => ['reports', 'config', reportType] as const,
  },
  
  // LOINC
  loinc: {
    all: ['loinc'] as const,
    search: (query: string, language: 'es' | 'en' = 'es') => 
      ['loinc', 'search', query, language] as const,
    term: (loincNum: string) => ['loinc', 'term', loincNum] as const,
    stats: ['loinc', 'stats'] as const,
    recent: ['loinc', 'recent'] as const,
  },
  
  // Admin
  admin: {
    all: ['admin'] as const,
    users: (filters?: any) => ['admin', 'users', filters] as const,
    user: (id: string) => ['admin', 'user', id] as const,
    institutions: (filters?: any) => ['admin', 'institutions', filters] as const,
    institution: (id: string) => ['admin', 'institution', id] as const,
    stats: ['admin', 'stats'] as const,
    auditLogs: (filters?: any) => ['admin', 'audit-logs', filters] as const,
  },

  // Worklist / daily agenda (session memory only — no localStorage PHI)
  worklist: {
    all: ['worklist'] as const,
    agenda: (date: string, physician?: string) =>
      ['worklist', 'agenda', date, physician ?? ''] as const,
    registry: ['worklist', 'registry'] as const,
  },
} as const;

// Medical-specific query options
export const medicalQueryOptions = {
  // Real-time medical data (measurements, vital signs)
  realTime: {
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
  },
  
  // DICOM image data (slower to change)
  dicomData: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },
  
  // LOINC terminology (rarely changes)
  terminology: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  },
  
  // Patient data (moderate update frequency)
  patientData: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  
  // Reports and documentation
  reports: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },
} as const;

// Error handling for medical API calls
export const handleMedicalApiError = (error: any) => {
  console.error('Medical API Error:', error);
  
  // Handle specific medical system errors
  if (error?.status === 422) {
    // Validation errors - preserve medical data integrity
    return {
      type: 'validation',
      message: 'Medical data validation failed',
      details: error.data?.detail || [],
    };
  }
  
  if (error?.status === 503) {
    // Service unavailable - critical for medical systems
    return {
      type: 'service_unavailable',
      message: 'Medical system temporarily unavailable',
    };
  }
  
  return {
    type: 'general',
    message: error?.message || 'An error occurred',
  };
};

// IndexedDB for robust offline storage
class MedicalOfflineDB {
  private dbName = 'gynhub-medical-offline';
  private version = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Store for offline patient data
        if (!db.objectStoreNames.contains('patients')) {
          const patientsStore = db.createObjectStore('patients', { keyPath: 'id' });
          patientsStore.createIndex('lastModified', 'lastModified');
        }
        
        // Store for pending medical operations
        if (!db.objectStoreNames.contains('pendingOperations')) {
          const pendingStore = db.createObjectStore('pendingOperations', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          pendingStore.createIndex('timestamp', 'timestamp');
          pendingStore.createIndex('type', 'type');
        }
        
        // Store for DICOM metadata cache
        if (!db.objectStoreNames.contains('dicomCache')) {
          const dicomStore = db.createObjectStore('dicomCache', { keyPath: 'studyId' });
          dicomStore.createIndex('patientId', 'patientId');
        }
        
        // Store for report drafts
        if (!db.objectStoreNames.contains('reportDrafts')) {
          const draftsStore = db.createObjectStore('reportDrafts', { keyPath: 'formId' });
          draftsStore.createIndex('patientId', 'patientId');
          draftsStore.createIndex('lastSaved', 'lastSaved');
        }
      };
    });
  }

  async storePatient(patient: any): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['patients'], 'readwrite');
    const store = transaction.objectStore('patients');
    
    const patientData = {
      ...patient,
      lastModified: Date.now(),
      cached: true
    };
    
    await store.put(patientData);
  }

  async getPatient(id: string): Promise<any> {
    if (!this.db) return null;
    
    const transaction = this.db.transaction(['patients'], 'readonly');
    const store = transaction.objectStore('patients');
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async storePendingOperation(operation: any): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['pendingOperations'], 'readwrite');
    const store = transaction.objectStore('pendingOperations');
    
    const operationData = {
      ...operation,
      timestamp: Date.now(),
      status: 'pending'
    };
    
    await store.add(operationData);
  }

  async getPendingOperations(): Promise<any[]> {
    if (!this.db) return [];
    
    const transaction = this.db.transaction(['pendingOperations'], 'readonly');
    const store = transaction.objectStore('pendingOperations');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removePendingOperation(id: number): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['pendingOperations'], 'readwrite');
    const store = transaction.objectStore('pendingOperations');
    
    await store.delete(id);
  }

  async storeReportDraft(formId: string, draftData: any): Promise<void> {
    if (!this.db) return;
    
    const transaction = this.db.transaction(['reportDrafts'], 'readwrite');
    const store = transaction.objectStore('reportDrafts');
    
    const draft = {
      formId,
      ...draftData,
      lastSaved: Date.now()
    };
    
    await store.put(draft);
  }

  async getReportDraft(formId: string): Promise<any> {
    if (!this.db) return null;
    
    const transaction = this.db.transaction(['reportDrafts'], 'readonly');
    const store = transaction.objectStore('reportDrafts');
    
    return new Promise((resolve, reject) => {
      const request = store.get(formId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}

// Global instance
export const medicalOfflineDB = new MedicalOfflineDB();

// Sync queue for pending operations
class MedicalSyncQueue {
  private queue: Array<{
    id: string;
    type: 'patient' | 'report' | 'measurement';
    operation: 'create' | 'update' | 'delete';
    data: any;
    timestamp: number;
  }> = [];

  async addToQueue(operation: any): Promise<void> {
    this.queue.push({
      id: generateUUID(),
      timestamp: Date.now(),
      ...operation
    });
    
    // Store in IndexedDB
    await medicalOfflineDB.storePendingOperation(operation);
    
    // Try immediate sync if online
    if (navigator.onLine) {
      this.processPendingOperations();
    }
  }

  async processPendingOperations(): Promise<void> {
    const pendingOps = await medicalOfflineDB.getPendingOperations();
    
    for (const operation of pendingOps) {
      try {
        await this.executeOperation(operation);
        await medicalOfflineDB.removePendingOperation(operation.id);
      } catch (error) {
        console.warn('Failed to sync operation:', operation, error);
        // Keep in queue for retry
      }
    }
  }

  private async executeOperation(operation: any): Promise<void> {
    const { type, operation: op, data } = operation;
    
    let endpoint = '';
    let method = 'POST';
    
    switch (type) {
      case 'patient':
        endpoint = op === 'create' ? '/api/v1/patients' : `/api/v1/patients/${data.id}`;
        method = op === 'create' ? 'POST' : op === 'update' ? 'PUT' : 'DELETE';
        break;
      case 'report':
        endpoint = op === 'create' ? '/api/v1/reports' : `/api/v1/reports/${data.id}`;
        method = op === 'create' ? 'POST' : op === 'update' ? 'PUT' : 'DELETE';
        break;
      case 'measurement':
        endpoint = '/api/v1/dicom/measurements';
        method = 'POST';
        break;
    }
    
    const requestInit: RequestInit = {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    };
    
    if (method !== 'DELETE') {
      requestInit.body = JSON.stringify(data);
    }
    
    const response = await fetch(endpoint, requestInit);
    
    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status}`);
    }
  }
}

export const medicalSyncQueue = new MedicalSyncQueue();

// Track initialization state to prevent duplicates
let isServiceWorkerInitialized = false;
let isOfflineDBInitialized = false;
let areListenersInitialized = false;

// Enhanced background sync for medical data - simplified to prevent multiple calls
export const setupMedicalDataSync = async () => {
  // Initialize IndexedDB only once
  if (!isOfflineDBInitialized) {
    try {
      await medicalOfflineDB.init();
      console.log('Medical offline database initialized');
      isOfflineDBInitialized = true;
    } catch (error) {
      console.error('Failed to initialize offline database:', error);
    }
  }

  // Register Service Worker only once
  if ('serviceWorker' in navigator && !isServiceWorkerInitialized) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Prevent aggressive SW updates
      });
      
      console.log('Service Worker registered:', registration);
      isServiceWorkerInitialized = true;
      
      // Setup background sync
      if ('sync' in registration) {
        try {
          await (registration as any).sync.register('medical-data-sync');
          await (registration as any).sync.register('report-drafts-sync');
          console.log('Background sync registered');
        } catch (error) {
          console.warn('Background sync not supported:', error);
        }
      }
      
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
  
  // Setup event listeners only once
  if (!areListenersInitialized) {
    // Listen for SW messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SYNC_REQUIRED') {
          medicalSyncQueue.processPendingOperations();
        }
      });
    }
    
    // Setup periodic sync when online
    window.addEventListener('online', () => {
      medicalSyncQueue.processPendingOperations();
    });
    
    areListenersInitialized = true;
  }
};

// Network status detection for medical workflows
export const setupNetworkDetection = () => {
  const updateOnlineStatus = () => {
    queryClient.setQueryData(['network', 'status'], {
      online: navigator.onLine,
      timestamp: Date.now(),
    });
  };

  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  
  // Initial status
  updateOnlineStatus();
  
  return () => {
    window.removeEventListener('online', updateOnlineStatus);
    window.removeEventListener('offline', updateOnlineStatus);
  };
};
